import type { MealType } from "../domain/model.js";
import { applyUserFoods, userFoodSearchResults } from "./user-foods";
import { askDuplicateFood, resolveDuplicateAdditions } from "./duplicate-food";
import { isMealTransferCommand } from "../../backend/src/rules/meal-transfer.js";
import { MealTransferError } from "./meal-transfer";
export { isMealNutritionComplete, isMealNutritionSaveable } from "./nutrition-completeness";
const API_ROOT = import.meta.env.DEV ? "/api" : "https://api.yumnote.ru";

export async function resolveUnknownFoods(draft: ParsedVoiceMealDraft): Promise<ParsedVoiceMealDraft> {
  const response = await fetch(`${API_ROOT}/v1/meal/options`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ draft, itemIndex: 0, key: "resolve-foods" }) });
  if (!response.ok) throw new Error("Не удалось проверить каталог. Состав не изменён. Попробуйте ещё раз.");
  return response.json() as Promise<ParsedVoiceMealDraft>;
}

export async function renameUnknownFood(draft: ParsedVoiceMealDraft, target: { itemIndex: number; componentIndex: number; name: string }, transcript: string): Promise<ParsedVoiceMealDraft> {
  const response = await fetch(`${API_ROOT}/v1/meal/options`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ draft, itemIndex: target.itemIndex, componentIndex: target.componentIndex, expectedName: target.name, key: "rename-unknown", transcript }) });
  if (!response.ok) {
    const result = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(result.error === "food_not_found" ? "По этому названию продукт тоже не найден. Попробуйте другой синоним или более простое название." : "Не удалось уточнить продукт. Запись не изменилась — попробуйте ещё раз.");
  }
  return response.json() as Promise<ParsedVoiceMealDraft>;
}

export type ParsedFoodUnit = "g" | "kg" | "ml" | "l" | "piece" | "portion" | "pack" | "can" | "glass" | "cup";

export interface ParsedVoiceFood {
  name: string;
  amount: number | null;
  unit: ParsedFoodUnit | null;
  sourceText: string;
  components?: Array<{ name: string; amount: number | null; unit: ParsedFoodUnit | null; catalogFoodId?: number }>;
  foodSettings?: Record<string, string | number>;
  catalogFoodId?: number;
  customRecipe?: boolean;
  portionResolution?: "scale-components" | "use-components";
}

export interface NutritionComponentEstimate {
  ignored?: boolean;
  name: string;
  amount: number | null;
  unit: string | null;
  grams: number | null;
  calories: number | null;
  protein: number | null;
  fat: number | null;
  carbs: number | null;
  valueSource: "stated" | "estimated" | "unknown";
}

export interface NutritionRowEstimate {
  name: string;
  portion: string;
  calories: number | null;
  protein: number;
  fat: number;
  carbs: number;
  valueSource: "stated" | "estimated";
  components: NutritionComponentEstimate[];
  isUserFood?: boolean;
  options?: FoodOptionGroup[];
  assumption?: string;
  help?: string;
  profileKey?: string;
  referenceFoodId?: number;
  categoryVersion?: string;
  kcal100?: number;
  kcalPortion?: number;
  macrosUnknown?: boolean;
}

export interface FoodOptionGroup {
  key: string;
  label: string;
  selected: string;
  choices: Array<{ value: string; label: string; kcal100?: number; protein100?: number }>;
}

export async function inspectFoodOptions(draft: ParsedVoiceMealDraft, itemIndex: number): Promise<{ options: FoodOptionGroup[]; help?: string }> {
  const response = await fetch(`${API_ROOT}/v1/meal/options`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ draft, itemIndex, key: "inspect" }) });
  if (!response.ok) throw new Error("Не удалось загрузить варианты.");
  return response.json();
}

export async function updateFoodOption(draft: ParsedVoiceMealDraft, itemIndex: number, key: string, value: string | number, transcript?: string): Promise<ParsedVoiceMealDraft> {
  const response = await fetch(`${API_ROOT}/v1/meal/options`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ draft, itemIndex, key, value, transcript }),
  });
  if (!response.ok) throw new Error(key === "recalculate" ? "Не для всех компонентов найден подходящий аналог. Выберите его в составе блюда или укажите калорийность всего блюда." : "Не удалось применить значение. Проверьте число и вес порции.");
  return response.json() as Promise<ParsedVoiceMealDraft>;
}

export interface MealClarification {
  itemIndex: number | null;
  field: "amount" | "unit" | "food" | "mealType" | "portionConflict";
  question: string;
  dishGrams?: number;
  componentTotal?: number;
}

export interface ParsedVoiceMealDraft {
  ingredientNoticeId?: string;
  foodLookupCompleted?: boolean;
  mealType: MealType;
  sourceText: string;
  items: ParsedVoiceFood[];
  clarifications: MealClarification[];
  nutrition?: { rows: NutritionRowEstimate[]; totalCalories: number; totalProtein: number; totalFat: number; totalCarbs: number };
}

export interface FoodSearchResult {
  id: number;
  name: string;
  matchedName: string;
  attribution?: string;
  nutrition: {
    foodId: number;
    name: string;
    grams: number;
    calories: number | null;
    protein: number | null;
    fat: number | null;
    carbs: number | null;
    unit: string;
    source: string;
  } | null;
  isUserFood?: boolean;
}

export async function searchFoodsRemotely(query: string): Promise<FoodSearchResult[]> {
  const response = await fetch(`${API_ROOT}/v1/foods/search?q=${encodeURIComponent(query)}`);
  if (!response.ok) throw new Error("food_search_failed");
  const result = await response.json() as { foods?: FoodSearchResult[] };
  const remote = (result.foods ?? []).filter(food => typeof food.nutrition?.calories === "number" && Number.isFinite(food.nutrition.calories) && food.nutrition.calories >= 0);
  const personal = userFoodSearchResults(query);
  const personalNames = new Set(personal.map(food => food.name.toLocaleLowerCase("ru-RU")));
  return [...personal, ...remote.filter(food => !personalNames.has((food.matchedName || food.name).toLocaleLowerCase("ru-RU")))];
}

export async function parseVoiceMealRemotely(sourceText: string, mealType: MealType): Promise<ParsedVoiceMealDraft> {
  if (isMealTransferCommand(sourceText)) throw new MealTransferError("Откройте исходный завтрак, обед или ужин и скажите команду переноса там.");
  const response = await fetch(`${API_ROOT}/v1/meal/parse`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceText, mealType }),
  });
  if (!response.ok) throw new Error("meal_parse_failed");
  return applyUserFoods(await response.json() as ParsedVoiceMealDraft);
}

export async function clarifyVoiceMealRemotely(draft: ParsedVoiceMealDraft, clarification: MealClarification, answer: string): Promise<ParsedVoiceMealDraft> {
  const response = await fetch(`${API_ROOT}/v1/meal/clarify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ draft, clarification, answer }),
  });
  if (!response.ok) throw new Error("meal_clarification_failed");
  return applyUserFoods(await response.json() as ParsedVoiceMealDraft);
}

export async function correctVoiceMealRemotely(draft: ParsedVoiceMealDraft, itemIndex: number | null, answer: string): Promise<ParsedVoiceMealDraft> {
  if (isMealTransferCommand(answer)) throw new MealTransferError("Для переноса всей еды используйте микрофон внизу открытого приёма пищи.");
  const proposed = await requestMealCorrection(draft, itemIndex, answer);
  return resolveDuplicateAdditions(draft, proposed, answer, askDuplicateFood, requestMealCorrection);
}

async function requestMealCorrection(draft: ParsedVoiceMealDraft, itemIndex: number | null, answer: string): Promise<ParsedVoiceMealDraft> {
  const response = await fetch(`${API_ROOT}/v1/meal/correct`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ draft, itemIndex, answer }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; errorId?: string };
    const error = new Error(body.error ?? "meal_correction_failed") as Error & { code?: string; errorId: string | undefined };
    error.code = body.error ?? "meal_correction_failed";
    error.errorId = body.errorId;
    throw error;
  }
  return applyUserFoods(await response.json() as ParsedVoiceMealDraft, draft);
}

export async function submitFoodFeedbackRemotely(draft: ParsedVoiceMealDraft, rowIndex: number, description: string): Promise<{ id: string; status: string }> {
  const response = await fetch(`${API_ROOT}/v1/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "food_catalog",
      description,
      context: {
        sourceText: draft.sourceText,
        mealType: draft.mealType,
        item: draft.items[rowIndex] ?? null,
        nutritionRow: draft.nutrition?.rows[rowIndex] ?? null,
        totals: draft.nutrition ? {
          calories: draft.nutrition.totalCalories,
          protein: draft.nutrition.totalProtein,
          fat: draft.nutrition.totalFat,
          carbs: draft.nutrition.totalCarbs,
        } : null,
      },
    }),
  });
  if (!response.ok) throw new Error("feedback_submission_failed");
  return response.json() as Promise<{ id: string; status: string }>;
}
